import { Chess, SQUARES, type Color, type PieceSymbol, type Square } from "chess.js";
import type { EvaluationScore } from "./evaluation";
import { formatEvaluation } from "./evaluation";
import { applySanMove, legalUciMoves } from "./rules";

export type MoveClassification = "forced" | "best" | "precise" | "playable" | "imprecision" | "mistake" | "blunder";

export interface MoveEvidenceProofStep {
  san: string;
  uci: string;
  evaluation: EvaluationScore | null;
}

export type MoveEvidenceFactKind =
  | "move"
  | "capture"
  | "check"
  | "checkmate"
  | "castle"
  | "promotion"
  | "development"
  | "support_added"
  | "support_removed"
  | "hanging_created"
  | "hanging_resolved"
  | "pin_created"
  | "pin_broken"
  | "fork_created"
  | "open_file"
  | "king_zone_pressure";

export interface MoveEvidenceFact {
  kind: MoveEvidenceFactKind;
  label: string;
  squares: readonly string[];
}

export interface MoveEvidenceMarker {
  kind: "played" | "best" | "relationship";
  source: "rules" | "stockfish";
  label: string;
  from?: string;
  to?: string;
  square?: string;
  factKind?: MoveEvidenceFactKind;
}

export interface MoveEvidenceInput {
  gameId: string;
  moveNumber: number;
  san: string;
  fenBefore: string;
  fenAfter: string;
  isPlayerMove: boolean;
  playerColor: "w" | "b" | null;
  evalBeforeCp: number | null;
  evalAfterCp: number | null;
  evalBeforeMate: number | null;
  evalAfterMate: number | null;
  centipawnLoss: number;
  bestMoveSan: string | null;
  bestMoveFrom: string | null;
  bestMoveTo: string | null;
  bestContinuation?: readonly string[] | null;
  bestContinuationScores?: readonly EvaluationScore[] | null;
  analysisDepth?: number | null;
}

export interface MoveEvidencePacket {
  version: 1;
  identity: {
    gameId: string;
    ply: number;
    moveNumber: number;
    mover: "player" | "opponent";
    fenBefore: string;
    fenAfter: string;
    san: string;
    playedUci: string;
  };
  objective: {
    evaluationBefore: EvaluationScore | null;
    evaluationAfter: EvaluationScore | null;
    centipawnLoss: number;
    classification: MoveClassification;
    bestMove: { san: string; uci: string } | null;
    proof: readonly MoveEvidenceProofStep[];
    analysisDepth: number | null;
  };
  facts: readonly MoveEvidenceFact[];
  markers: readonly MoveEvidenceMarker[];
  summary: string;
}

const normalizedScore = (cp: number | null, mate: number | null, multiplier: 1 | -1): EvaluationScore | null => {
  if (mate != null && Number.isFinite(mate)) return { cp: null, mate: mate * multiplier };
  if (cp == null || !Number.isFinite(cp) || Math.abs(cp) === 10_000) return null;
  return { cp: cp * multiplier, mate: null };
};

const classificationFor = (
  legalMoveCount: number,
  playedUci: string,
  bestUci: string | null,
  centipawnLoss: number,
  before: EvaluationScore | null,
  after: EvaluationScore | null,
): MoveClassification => {
  if (legalMoveCount === 1) return "forced";
  if (bestUci === playedUci) return "best";
  if (before?.mate != null && before.mate > 0 && (after?.mate == null || after.mate < 0)) return "blunder";
  if (after?.mate != null && after.mate < 0 && (before?.mate == null || before.mate >= 0)) return "blunder";
  const loss = Math.max(0, centipawnLoss);
  if (loss < 15) return "precise";
  if (loss < 50) return "playable";
  if (loss < 100) return "imprecision";
  if (loss < 200) return "mistake";
  return "blunder";
};

export const legalBestMove = (
  input: Pick<MoveEvidenceInput, "fenBefore" | "bestMoveSan" | "bestMoveFrom" | "bestMoveTo">,
): { san: string; uci: string } | null => {
  if (!input.bestMoveSan) return null;
  const applied = applySanMove(input.fenBefore, input.bestMoveSan);
  if (applied?.san !== input.bestMoveSan) return null;
  if (input.bestMoveFrom && !applied.uci.startsWith(input.bestMoveFrom)) return null;
  if (input.bestMoveTo && applied.uci.slice(2, 4) !== input.bestMoveTo) return null;
  return { san: applied.san, uci: applied.uci };
};

const bestProof = (
  input: MoveEvidenceInput,
  bestMove: { san: string; uci: string } | null,
): readonly MoveEvidenceProofStep[] => {
  if (!bestMove) return [];
  const continuation = input.bestContinuation ?? [];
  const aligned = input.bestContinuationScores?.length === continuation.length;
  const moverMultiplier: 1 | -1 = input.isPlayerMove ? 1 : -1;
  const line = [bestMove.san, ...continuation].slice(0, 4);
  const steps: MoveEvidenceProofStep[] = [];
  let fen = input.fenBefore;
  for (let index = 0; index < line.length; index += 1) {
    const applied = applySanMove(fen, line[index]);
    if (!applied) break;
    steps.push({
      san: applied.san,
      uci: applied.uci,
      evaluation:
        index === 0 || !aligned
          ? null
          : normalizedScore(
              input.bestContinuationScores![index - 1].cp,
              input.bestContinuationScores![index - 1].mate,
              moverMultiplier,
            ),
    });
    fen = applied.fen;
  }
  return steps;
};

type PositionedPiece = { square: string; color: Color; type: PieceSymbol };

const boardPieces = (chess: Chess): readonly PositionedPiece[] =>
  SQUARES.flatMap((square) => {
    const piece = chess.get(square);
    return piece ? [{ square, color: piece.color, type: piece.type }] : [];
  });

const defendedPieces = (chess: Chess, color: Color): Set<string> =>
  new Set(
    boardPieces(chess)
      .filter((piece) => piece.color === color && piece.type !== "k" && chess.isAttacked(piece.square as Square, color))
      .map((piece) => piece.square),
  );

const hangingPieces = (chess: Chess, color: Color): Set<string> => {
  const opponent: Color = color === "w" ? "b" : "w";
  return new Set(
    boardPieces(chess)
      .filter((piece) => piece.color === color && piece.type !== "k")
      .filter(
        (piece) =>
          chess.isAttacked(piece.square as Square, opponent) && !chess.isAttacked(piece.square as Square, color),
      )
      .map((piece) => piece.square),
  );
};

const squareAt = (file: number, rank: number): Square | null =>
  file >= 0 && file < 8 && rank >= 0 && rank < 8 ? (`${String.fromCharCode(97 + file)}${rank + 1}` as Square) : null;

const coordinates = (square: string): readonly [number, number] => [square.charCodeAt(0) - 97, Number(square[1]) - 1];

const attacksFrom = (chess: Chess, square: string): Set<string> => {
  const piece = chess.get(square as Square);
  if (!piece) return new Set();
  return new Set(SQUARES.filter((target) => chess.attackers(target, piece.color).includes(square as Square)));
};

const absolutePins = (chess: Chess, color: Color): Set<string> => {
  const king = boardPieces(chess).find((piece) => piece.color === color && piece.type === "k");
  if (!king) return new Set();
  const [file, rank] = coordinates(king.square);
  const pins = new Set<string>();
  const directions = [
    [1, 1],
    [1, -1],
    [-1, 1],
    [-1, -1],
    [1, 0],
    [-1, 0],
    [0, 1],
    [0, -1],
  ];
  for (const [df, dr] of directions) {
    let candidate: string | null = null;
    for (let distance = 1; distance < 8; distance += 1) {
      const square = squareAt(file + df * distance, rank + dr * distance);
      if (!square) break;
      const piece = chess.get(square);
      if (!piece) continue;
      if (!candidate && piece.color === color && piece.type !== "k") {
        candidate = square;
        continue;
      }
      if (candidate && piece.color !== color) {
        const diagonal = df !== 0 && dr !== 0;
        if (piece.type === "q" || (diagonal ? piece.type === "b" : piece.type === "r")) pins.add(candidate);
      }
      break;
    }
  }
  return pins;
};

const setDifference = (left: ReadonlySet<string>, right: ReadonlySet<string>): readonly string[] =>
  [...left].filter((value) => !right.has(value)).sort();

const openFiles = (chess: Chess): Set<string> =>
  new Set(
    "abcdefgh"
      .split("")
      .filter((file) => !boardPieces(chess).some((piece) => piece.type === "p" && piece.square[0] === file)),
  );

const observedFacts = (input: MoveEvidenceInput, playedUci: string): readonly MoveEvidenceFact[] => {
  const before = new Chess(input.fenBefore);
  const after = new Chess(input.fenAfter);
  const mover = input.fenBefore.split(" ")[1] as Color;
  const opponent: Color = mover === "w" ? "b" : "w";
  const from = playedUci.slice(0, 2);
  const to = playedUci.slice(2, 4);
  const movingPiece = before.get(from as Square);
  const facts: MoveEvidenceFact[] = [];
  const add = (kind: MoveEvidenceFactKind, label: string, squares: readonly string[]) =>
    facts.push({ kind, label, squares });

  if (input.san.endsWith("#")) add("checkmate", "Delivered checkmate", [to]);
  else if (input.san.endsWith("+")) add("check", "Gave check", [to]);
  if (input.san.includes("=", 1)) add("promotion", `Promoted on ${to}`, [to]);
  if (input.san.startsWith("O-O")) add("castle", "Castled the king", [from, to]);
  if (input.san.includes("x")) add("capture", `Captured on ${to}`, [to]);

  const forkTargets = boardPieces(after).filter(
    (piece) => piece.color === opponent && piece.type !== "p" && attacksFrom(after, to).has(piece.square),
  );
  if (forkTargets.length >= 2)
    add(
      "fork_created",
      "Attacked multiple valuable pieces",
      forkTargets.map((piece) => piece.square),
    );

  const pinsBefore = absolutePins(before, opponent);
  const pinsAfter = absolutePins(after, opponent);
  for (const square of setDifference(pinsAfter, pinsBefore))
    add("pin_created", `Pinned the piece on ${square}`, [square]);
  for (const square of setDifference(pinsBefore, pinsAfter))
    add("pin_broken", `Released the pin on ${square}`, [square]);

  const hangingBefore = hangingPieces(before, mover);
  const hangingAfter = hangingPieces(after, mover);
  for (const square of setDifference(hangingAfter, hangingBefore))
    add("hanging_created", `Left the piece on ${square} loose`, [square]);
  for (const square of setDifference(hangingBefore, hangingAfter))
    add("hanging_resolved", `Secured the piece on ${square}`, [square]);

  const defendedBefore = defendedPieces(before, mover);
  const defendedAfter = defendedPieces(after, mover);
  for (const square of setDifference(defendedAfter, defendedBefore))
    add("support_added", `Added support to ${square}`, [square]);
  for (const square of setDifference(defendedBefore, defendedAfter))
    add("support_removed", `Removed support from ${square}`, [square]);

  for (const file of setDifference(openFiles(after), openFiles(before)))
    add("open_file", `Opened the ${file}-file`, [`${file}1`, `${file}8`]);

  const enemyKing = boardPieces(after).find((piece) => piece.color === opponent && piece.type === "k");
  if (enemyKing && movingPiece) {
    const [kingFile, kingRank] = coordinates(enemyKing.square);
    const zone = new Set<string>();
    for (let df = -1; df <= 1; df += 1)
      for (let dr = -1; dr <= 1; dr += 1) {
        const square = squareAt(kingFile + df, kingRank + dr);
        if (square) zone.add(square);
      }
    const pressureBefore = [...attacksFrom(before, from)].filter((square) => zone.has(square)).length;
    const pressureAfter = [...attacksFrom(after, to)].filter((square) => zone.has(square)).length;
    if (pressureAfter > pressureBefore) add("king_zone_pressure", "Added pressure near the king", [to]);
  }

  const home = mover === "w" ? new Set(["b1", "c1", "f1", "g1"]) : new Set(["b8", "c8", "f8", "g8"]);
  if (movingPiece && (movingPiece.type === "n" || movingPiece.type === "b") && home.has(from)) {
    add("development", `Developed the ${movingPiece.type === "n" ? "knight" : "bishop"}`, [from, to]);
  }
  if (facts.length === 0) add("move", `Moved to ${to}`, [from, to]);
  return facts.slice(0, 4);
};

const markersFor = (
  playedUci: string,
  bestMove: { san: string; uci: string } | null,
  facts: readonly MoveEvidenceFact[],
): readonly MoveEvidenceMarker[] => {
  const markers: MoveEvidenceMarker[] = [
    {
      kind: "played",
      source: "rules",
      label: "Played move",
      from: playedUci.slice(0, 2),
      to: playedUci.slice(2, 4),
    },
  ];
  if (bestMove && bestMove.uci !== playedUci)
    markers.push({
      kind: "best",
      source: "stockfish",
      label: "Verified engine move",
      from: bestMove.uci.slice(0, 2),
      to: bestMove.uci.slice(2, 4),
    });
  for (const fact of facts) {
    const square = fact.squares[0];
    if (!square || markers.length >= 4) break;
    markers.push({ kind: "relationship", source: "rules", label: fact.label, square, factKind: fact.kind });
  }
  return markers.slice(0, 4);
};

const summaryFor = (
  san: string,
  classification: MoveClassification,
  before: EvaluationScore | null,
  after: EvaluationScore | null,
): string => {
  const result =
    classification === "best"
      ? `${san} matched the verified engine move.`
      : classification === "forced"
        ? `${san} was forced.`
        : `${san} was ${classification === "imprecision" ? "an" : "a"} ${classification}.`;
  if (!before || !after) return result;
  return `${result} Your evaluation changed from ${formatEvaluation(before)} to ${formatEvaluation(after)}.`;
};

export const buildMoveEvidence = (input: MoveEvidenceInput): MoveEvidencePacket | null => {
  const fields = input.fenBefore.split(" ");
  const turn = fields[1];
  const fullmove = Number(fields[5]);
  if ((turn !== "w" && turn !== "b") || !Number.isInteger(fullmove) || fullmove < 1) return null;
  if (input.moveNumber !== fullmove || !Number.isFinite(input.centipawnLoss) || input.centipawnLoss < 0) return null;

  const applied = applySanMove(input.fenBefore, input.san);
  if (applied?.san !== input.san || applied.fen !== input.fenAfter) return null;

  if (input.playerColor != null && (turn === input.playerColor) !== input.isPlayerMove) return null;

  const before = normalizedScore(input.evalBeforeCp, input.evalBeforeMate, input.isPlayerMove ? 1 : -1);
  const after = normalizedScore(input.evalAfterCp, input.evalAfterMate, input.isPlayerMove ? -1 : 1);
  const bestMove = legalBestMove(input);
  const classification = classificationFor(
    legalUciMoves(input.fenBefore).length,
    applied.uci,
    bestMove?.uci ?? null,
    input.centipawnLoss,
    normalizedScore(input.evalBeforeCp, input.evalBeforeMate, 1),
    normalizedScore(input.evalAfterCp, input.evalAfterMate, -1),
  );
  const facts = observedFacts(input, applied.uci);

  return {
    version: 1,
    identity: {
      gameId: input.gameId,
      ply: (fullmove - 1) * 2 + (turn === "w" ? 1 : 2),
      moveNumber: fullmove,
      mover: input.isPlayerMove ? "player" : "opponent",
      fenBefore: input.fenBefore,
      fenAfter: input.fenAfter,
      san: applied.san,
      playedUci: applied.uci,
    },
    objective: {
      evaluationBefore: before,
      evaluationAfter: after,
      centipawnLoss: bestMove?.uci === applied.uci ? 0 : input.centipawnLoss,
      classification,
      bestMove,
      proof: bestProof(input, bestMove),
      analysisDepth: Number.isInteger(input.analysisDepth) && input.analysisDepth! > 0 ? input.analysisDepth! : null,
    },
    facts,
    markers: markersFor(applied.uci, bestMove, facts),
    summary: summaryFor(applied.san, classification, before, after),
  };
};
